import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiTypes } from '@mode/shared/contract-common';
import { getTokenFromModeLink, getTypeFromModeLink } from '@mode/shared/util-halogen';
import * as angular from 'angular';
import { QAdapterService } from '../migration/q-adapter.service';
import { LinkParameters } from './mode-link.factory';

export interface ResourceOptions {
  relationAs?: string; // optional name mapping to backend resource
  includes?: string | any[] | {};
  /**
   * optional key value map for templated backend endpoints. remember
   * that the key must match a target token in the endpoint template
   * in order for the value to be included in the "expanded" path
   */
  expand?: { [key: string]: string };
}

/**
 * @deprecated Migrate to ApiService in @mode/shared/util-halogen or a standalone client service
 */
@Injectable({ providedIn: 'root' })
export class DeprecatedApiService {
  constructor(private $q: QAdapterService) {}

  getSelf<T extends ApiTypes.ModeResource>(resource: T, options?: ResourceOptions): angular.IPromise<T> {
    if (resource.hasLink('self')) {
      const params: LinkParameters = this.getParamsHelper(options);
      return resource.links('self').$follow(params);
    }

    return this.$q.reject(new NoResourceError(resource, 'self'));
  }

  getResource<U extends ApiTypes.ModeResource>(
    resource: ApiTypes.ModeResource,
    rel: string,
    options?: ResourceOptions
  ): angular.IPromise<U> {
    if (this.canGetEmbedded(resource, rel, options)) {
      return this.$q.resolve(resource.embedded(rel));
    }

    if (resource.hasLink(rel)) {
      const params: LinkParameters = this.getParamsHelper(options);
      return resource.links(rel).$follow(params);
    }

    return this.$q.reject(new NoResourceError(resource, rel));
  }

  getResources<U extends ApiTypes.ModeResource>(
    resource: ApiTypes.ModeResource,
    rel: string,
    options?: ResourceOptions
  ): angular.IPromise<U[]> {
    if (this.canGetEmbedded(resource, rel, options)) {
      return this.$q.resolve(resource.embedded(rel)._embedded[(options && options.relationAs) || rel]);
    }

    if (resource.hasLink(rel)) {
      const params: LinkParameters = this.getParamsHelper(options);
      return resource
        .links(rel)
        .$follow(params)
        .then((res: ApiTypes.ModeResource) => res.embedded((options && options.relationAs) || rel));
    }

    return this.$q.reject(new NoResourceError(resource, rel));
  }

  create<R extends ApiTypes.ModeResource>(
    resource: ApiTypes.ModeResource,
    rel: string,
    params: any
  ): angular.IPromise<R> {
    if (this.canGetEmbedded(resource, rel)) {
      const embed = resource.embedded(rel);
      if (embed.hasForm('create')) {
        return embed.forms('create').$submit(params);
      } else {
        return this.$q.reject(new NoResourceError(embed, 'create'));
      }
    }

    return this.$q.reject(new NoResourceError(resource, rel));
  }

  destroy<U extends ApiTypes.ModeResource>(resource: U, params: any) {
    if (resource.hasForm('destroy')) {
      return resource.forms('destroy').$submit(params);
    } else {
      return this.$q.reject(new NoResourceError(resource, 'destroy'));
    }
  }

  edit<U extends ApiTypes.ModeResource>(resource: U, params: any): angular.IPromise<U> {
    if (resource.hasForm('edit')) {
      return resource.forms('edit').$submit(params);
    } else {
      return this.$q.reject(new NoResourceError(resource, 'edit'));
    }
  }

  private canGetEmbedded(resource: ApiTypes.ModeResource, rel: string, options?: ResourceOptions) {
    // dont use embedded resource if includes option is passed since we need to enforce embedded resources in response
    return resource.hasEmbedded(rel) && (!options || !options.includes);
  }

  /**
   * Extract "includes" for embed parameters and "expand" values
   * for backend endpoint template interpolation/expansion.
   */
  private getParamsHelper(options?: ResourceOptions): LinkParameters {
    let params: LinkParameters = {};
    if (options && options.includes) {
      params.includes = options.includes;
    }

    if (options && options.expand) {
      params = { ...params, ...options.expand };
    }

    return params;
  }
}

export class ApiError extends Error {
  status: number;
  statusText: string;

  constructor(response: HttpErrorResponse) {
    super(response.message);
    (this.status = response.status), (this.statusText = response.statusText);
  }
}

export class NoResourceError extends Error {
  constructor(resource: ApiTypes.ModeResource, rel: string) {
    const type = getTypeFromModeLink(resource.links('self'));
    const token = getTokenFromModeLink(resource.links('self'));
    super(`${type || 'Resource'} ${token} has no link '${rel}'`);
  }
}
